/**
 * Shared mock payloads for devState previews. Not imported in production after strip.
 * See docs/build/DEV_PREVIEW_STRIP.md
 */

// @mcc-dev-preview:start dev-preview-mocks
import { CardState } from '../types/cardTypes';
import { DEV_CARD_ID } from './devPreview';

export { DEV_CARD_ID };

export const DEV_PREVIEW_ACTIVATED_CARD = {
  cardId: DEV_CARD_ID,
  state: CardState.ACTIVATED,
  balance: '0.01250000',
  coin: 'Bitcoin',
  coinSymbol: 'BTC',
  originalAmount: '100',
  cardCreateDate: null,
  createdAt: '2024-01-01T00:00:00.000Z',
  updatedAt: '2024-01-01T00:00:00.000Z',
};

export const DEV_PREVIEW_NEW_CARD = {
  ...DEV_PREVIEW_ACTIVATED_CARD,
  state: CardState.NEW,
  balance: '0',
};

export const DEV_PREVIEW_USER = {
  id: 'dev-user',
  mobileNumber: '+61400000000',
  firstName: 'Alex',
  lastName: 'Preview',
  status: 'active',
  createdAt: '2024-01-01T00:00:00.000Z',
  updatedAt: '2024-01-01T00:00:00.000Z',
};

export const DEV_PREVIEW_SELL_QUOTES = [
  {
    paymentMethodId: 'payid-bank-transfer',
    fiatCode: 'AUD',
    fiatAmount: 1247.5,
    processingFee: 12.48,
    networkFee: 3.2,
    paymentMethodName: 'PayID Bank Transfer',
  },
];

export const DEV_PREVIEW_BANXA_STATUS_URL =
  'https://mycryptocard.banxa-sandbox.com/status/dev-preview-order';

export const DEV_PREVIEW_BALANCE_CARD_DETAILS = {
  cardId: DEV_CARD_ID,
  balance: '0.002',
  balanceAUD: '6.60',
  // $100 spend minus $2.00 processing fee and $0.50 network fee (see PurchaseCrypto's "Crypto spend")
  originalAmount: '97.50',
  originalAmountAUD: '97.50',
  coin: 'Bitcoin',
  coinSymbol: 'BTC',
  state: CardState.ACTIVATED,
};

export const DEV_PREVIEW_CRYPTO_PRODUCTS = [
  {
    id: 'btc',
    name: 'Bitcoin',
    symbol: 'BTC',
    purchaseOptions: [100, 150, 200, 250, 300, 350, 400, 450, 500, 550, 600, 650, 700, 750, 800, 850, 900, 950, 1000],
    decimalPlaces: 8,
    description: 'Bitcoin',
  },
];

// $100 – $1000 in $50 increments, matching DEV_PREVIEW_CRYPTO_PRODUCTS' purchaseOptions
const DEV_PREVIEW_AMOUNTS = Array.from({ length: 19 }, (_, i) => 100 + i * 50);
const BTC_PER_AUD = 0.0015 / 100;

export const DEV_PREVIEW_BANXA_PRICES = {
  success: true,
  data: {
    quotes: DEV_PREVIEW_AMOUNTS.map((amount) => ({
      amount,
      prices: [
        {
          paymentMethodId: 'payid-bank-transfer',
          cryptoAmount: amount * BTC_PER_AUD,
          fiatAmount: amount,
          processingFee: 2,
          networkFee: 0.5,
          coinCode: 'BTC',
          fiatCode: 'AUD',
          blockchain: 'bitcoin',
        },
      ],
    })),
    paymentMethods: [
      {
        id: 'payid-bank-transfer',
        name: 'PayID Bank Transfer',
        description: 'Instant bank transfer',
        supportedFiats: ['AUD'],
      },
    ],
  },
};

export const DEV_PREVIEW_BANXA_PRICES_FOUR_PM = {
  success: true,
  data: {
    quotes: DEV_PREVIEW_AMOUNTS.map((amount) => ({
      amount,
      prices: [
        {
          paymentMethodId: 'payid-bank-transfer',
          cryptoAmount: amount * BTC_PER_AUD,
          fiatAmount: amount,
          processingFee: 2,
          networkFee: 0.5,
          coinCode: 'BTC',
          fiatCode: 'AUD',
          blockchain: 'bitcoin',
        },
        {
          paymentMethodId: 'debit-credit-card',
          cryptoAmount: amount * BTC_PER_AUD,
          fiatAmount: amount,
          processingFee: 3.5,
          networkFee: 0.5,
          coinCode: 'BTC',
          fiatCode: 'AUD',
          blockchain: 'bitcoin',
        },
        {
          paymentMethodId: 'apple-pay',
          cryptoAmount: amount * BTC_PER_AUD,
          fiatAmount: amount,
          processingFee: 4,
          networkFee: 0.5,
          coinCode: 'BTC',
          fiatCode: 'AUD',
          blockchain: 'bitcoin',
        },
        {
          paymentMethodId: 'google-pay',
          cryptoAmount: amount * BTC_PER_AUD,
          fiatAmount: amount,
          processingFee: 4.5,
          networkFee: 0.5,
          coinCode: 'BTC',
          fiatCode: 'AUD',
          blockchain: 'bitcoin',
        },
      ],
    })),
    paymentMethods: [
      { id: 'payid-bank-transfer', name: 'PayID', description: 'PayID', supportedFiats: ['AUD'] },
      { id: 'debit-credit-card', name: 'Credit Card', description: 'Credit Card', supportedFiats: ['AUD'] },
      { id: 'apple-pay', name: 'Apple Pay', description: 'Apple Pay', supportedFiats: ['AUD'] },
      { id: 'google-pay', name: 'Google Pay', description: 'Google Pay', supportedFiats: ['AUD'] },
    ],
  },
};

export const DEV_PREVIEW_CHECKOUT_ORDER = {
  id: 'dev-order',
  cardId: DEV_CARD_ID,
  cryptoType: 'BTC',
  amount: 100,
  cryptoAmount: 0.0015,
  fee: 2.5,
  totalCost: 100,
  status: 'pending',
  isGiftCard: false,
  createdAt: new Date('2024-01-01T00:00:00.000Z'),
};

export const DEV_PREVIEW_FINALISE_SELL = {
  cardId: DEV_CARD_ID,
  banxaOrderId: 'dev-sell-order-001',
  internalOrderId: 'dev-sell-order-001',
  blockchain: 'BTC',
  coinType: 'BTC',
  coinAmount: '0.01',
  preparedCryptoAmount: 0.00995,
  preparedAt: '2024-06-01T12:00:00.000Z',
  prepareNote: 'Dev mock — fee reserve applied for preview.',
  pinMethod: { id: 'dev-pin-method-1', type: 'PIN', name: 'PIN' },
};

export const DEV_PREVIEW_BIOMETRIC_METHODS = [
  {
    signingMethodId: 'dev-bio-1',
    deviceType: 'fingerprint',
    deviceName: 'Preview Device',
    platform: 'macOS',
    createdAt: '2024-01-01T00:00:00.000Z',
  },
];
// @mcc-dev-preview:end dev-preview-mocks
